import { useMutation, useQueryClient } from '@tanstack/react-query';

import { toast } from '@hivebrite/design-system/Toast';

import {
  createMentoringProfile,
  editMentoringProfile,
} from 'hb-react/frontoffice/apps/Mentoring/requests';
import { type Program } from 'hb-react/frontoffice/apps/Mentoring/types';
import { getProgramTopicId } from 'hb-react/frontoffice/apps/Mentoring/utils';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { QUERY_KEYS } from 'hb-react/shared/apps/Mentoring/constants';
import { type CustomAttributeFormValue } from 'hb-react/types';

export const useCreateMentor = (
  program: Pick<Program, 'id' | 'parent_id' | 'parent_type'>,
) => {
  const {
    id: programId,
    parent_id: parentId,
    parent_type: parentType,
  } = program;
  const { currentUser } = useDefaultAppContexts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { [s: string]: CustomAttributeFormValue }) =>
      createMentoringProfile({
        routeApi: Routes.mentorings_program_mentor_profiles_path(programId),
        payload: {
          mentor_profile: {
            ...payload,
          },
        },
        topicId: getProgramTopicId({ parentId, parentType }),
      }),
    onSuccess: (updatedProfile) => {
      if (currentUser) {
        queryClient.setQueryData(
          [QUERY_KEYS.mentorProfileFromUser, programId, currentUser.id],
          updatedProfile,
        );
      }
    },
    onError: (error) => {
      // @ts-expect-error untyped error
      if (error?.response?.status === 400) {
        toast.error(s__('Mentoring|You have already applied'));
      } else {
        toast.error(__('Sorry, an error occurred'));
      }
    },
  });
};

interface EditMentorProps {
  mentorId: number;
  program: Pick<Program, 'id' | 'parent_id' | 'parent_type'>;
}

export const useEditMentor = ({ program, mentorId }: EditMentorProps) => {
  const {
    id: programId,
    parent_id: parentId,
    parent_type: parentType,
  } = program;
  const { currentUser } = useDefaultAppContexts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { [s: string]: CustomAttributeFormValue }) =>
      editMentoringProfile({
        routeApi: Routes.mentorings_program_mentor_profile_path(
          programId,
          mentorId,
        ),
        payload: {
          mentor_profile: {
            ...payload,
          },
        },
        topicId: getProgramTopicId({ parentId, parentType }),
      }),
    onSuccess: (updatedProfile) => {
      if (currentUser) {
        queryClient.setQueryData(
          [QUERY_KEYS.mentorProfileFromUser, programId, currentUser.id],
          updatedProfile,
        );
        queryClient.setQueryData(
          [QUERY_KEYS.mentorProfile, programId, mentorId],
          updatedProfile,
        );
      }
    },
    onError: () => {
      toast.error(__('Sorry, an error occurred'));
    },
  });
};
